import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { JsonLocalizationService } from './JsonLocalizationService';
import { loadJsonLocalizationDictionaries } from './loadResources';
import { loadTenantConfigurations } from './loadTenants';
import {
  createLocalizationContext,
  LocalizationService,
} from './LocalizationService';
import { resolveLocalizationRequest } from './middleware/resolveLocalizationRequest';
import { TenantTerminologyService } from './TenantTerminologyService';
import type { ILocalizationService } from './types';

let singleton: LocalizationService | null = null;

export function getLocalizationService(): LocalizationService {
  if (!singleton) {
    throw new Error('Localization not bootstrapped. Call bootstrapLocalization() first.');
  }
  return singleton;
}

/**
 * Uygulama başlangıcında bir kez çağrılır.
 * JSON sözlükler + tenant override + i18next senkronizasyonu.
 */
export async function bootstrapLocalization(): Promise<{
  i18n: typeof i18n;
  localization: ILocalizationService;
}> {
  const json = new JsonLocalizationService();
  loadJsonLocalizationDictionaries(json);

  const tenantService = new TenantTerminologyService();
  loadTenantConfigurations(tenantService);

  const { context } = resolveLocalizationRequest();
  const localization = new LocalizationService(json, tenantService, context);
  singleton = localization;

  const resources = localization.buildI18nResources();

  if (!i18n.isInitialized) {
    await i18n.use(initReactI18next).init({
      lng: context.language,
      fallbackLng: 'tr',
      debug: false,
      resources: {
        tr: { translation: resources.tr },
        en: { translation: resources.en },
      },
      interpolation: { escapeValue: false },
    });
  } else {
    for (const lang of ['tr', 'en'] as const) {
      i18n.addResourceBundle(lang, 'translation', resources[lang], true, true);
    }
    await i18n.changeLanguage(context.language);
  }

  i18n.on('languageChanged', (lng) => {
    const language = lng === 'en' ? 'en' : 'tr';
    localization.setLanguage(language);
  });

  return { i18n, localization };
}

/** Tenant değişince i18next bundle'ı yeniden merge eder */
export async function switchTenant(tenantId: string): Promise<void> {
  const service = getLocalizationService();
  await service.setTenant(tenantId);
  const resources = service.buildI18nResources();
  for (const lang of ['tr', 'en'] as const) {
    i18n.addResourceBundle(lang, 'translation', resources[lang], true, true);
  }
  await i18n.changeLanguage(service.context.language);
}

export { createLocalizationContext };
